import React, { useState } from 'react';
import { MapPin, Phone, Mail, MessageCircle, Copy, Check, QrCode } from 'lucide-react';
import Breadcrumbs from './Navigation/Breadcrumbs';
import RequestForm from './services/RequestForm';
import { useLanguage } from '../contexts/LanguageContext';

function ContactsPage() {
  const { t, language } = useLanguage();
  const [copiedCn, setCopiedCn] = useState(false);
  const [copiedEn, setCopiedEn] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);

  const addresses = {
    cn: '广州市荔湾区站前路113号7-8档',
    en: '7-8 Gears, No. 113 Zhanqian Road, Liwan District, Guangzhou.'
  };

  const handleCopy = async (text: string, type: 'cn' | 'en') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'cn') {
        setCopiedCn(true);
        setTimeout(() => setCopiedCn(false), 2000);
      } else {
        setCopiedEn(true);
        setTimeout(() => setCopiedEn(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-[#1B3BA0]/50 mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#1B3BA0]/20"></div>
        </div>
        <div className="max-w-[980px] mx-auto">
          <div className="max-w-[780px] relative">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
              <span className="text-white block mb-2">
                {language === 'ru' ? 'Контакты' : 
                 language === 'en' ? 'Contacts' : 
                 '联系方式'}
              </span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-[#99BBFF] animate-gradient-wave">
                {language === 'ru' ? 'для связи с нами' : 
                 language === 'en' ? 'to get in touch with us' : 
                 '与我们联系'}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed">
              {language === 'ru' 
                ? 'Выберите удобный способ связи с нашей командой'
                : language === 'en' 
                ? 'Choose a convenient way to contact our team'
                : '选择一种方便的方式联系我们的团队'}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-24 px-6 bg-white relative z-10 -mt-20">
        <div className="max-w-[980px] mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl border border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Address Card */}
            <div className="bg-[#1B3BA0]/5 rounded-2xl p-8 hover:bg-[#1B3BA0]/10 transition-all duration-300">
              <h2 className="text-xl font-bold text-[#1B3BA0] mb-6 flex items-center">
                <MapPin className="w-6 h-6 text-[#1B3BA0] mr-2" />
                {language === 'ru' ? 'Адрес офиса' : 
                 language === 'en' ? 'Office Address' : 
                 '办公地址'}
              </h2>
              
              {/* Chinese Address */}
              <div className="mb-6">
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-2">🇨🇳</span>
                  <span className="text-gray-600">
                    {language === 'ru' ? 'На китайском языке' : 
                     language === 'en' ? 'In Chinese' : 
                     '中文'}
                  </span>
                </div>
                <div className="bg-white rounded-xl p-4 mb-2 shadow-md">
                  <p className="text-gray-800 font-medium">{addresses.cn}</p>
                </div>
                <button
                  onClick={() => handleCopy(addresses.cn, 'cn')}
                  className="inline-flex items-center px-4 py-2 rounded-full bg-white text-[#1B3BA0] hover:bg-[#1B3BA0] hover:text-white transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  {copiedCn ? (
                    <Check className="w-4 h-4 mr-2" />
                  ) : (
                    <Copy className="w-4 h-4 mr-2" />
                  )}
                  {copiedCn 
                    ? (language === 'ru' ? 'Скопировано!' : language === 'en' ? 'Copied!' : '已复制！') 
                    : (language === 'ru' ? 'Скопировать' : language === 'en' ? 'Copy' : '复制')}
                </button>
              </div>

              {/* English Address */}
              <div>
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-2">🇬🇧</span>
                  <span className="text-gray-600">
                    {language === 'ru' ? 'На английском языке' : 
                     language === 'en' ? 'In English' : 
                     '英文'}
                  </span>
                </div>
                <div className="bg-white rounded-xl p-4 mb-2 shadow-md">
                  <p className="text-gray-800 font-medium">{addresses.en}</p>
                </div>
                <button
                  onClick={() => handleCopy(addresses.en, 'en')}
                  className="inline-flex items-center px-4 py-2 rounded-full bg-white text-[#1B3BA0] hover:bg-[#1B3BA0] hover:text-white transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  {copiedEn ? (
                    <Check className="w-4 h-4 mr-2" />
                  ) : (
                    <Copy className="w-4 h-4 mr-2" />
                  )}
                  {copiedEn 
                    ? (language === 'ru' ? 'Скопировано!' : language === 'en' ? 'Copied!' : '已复制！') 
                    : (language === 'ru' ? 'Скопировать' : language === 'en' ? 'Copy' : '复制')}
                </button>
              </div>
            </div>

            {/* Phone & Email Card */}
            <div className="space-y-8">
              {/* Phone */}
              <div className="bg-[#1B3BA0]/5 rounded-2xl p-8 hover:bg-[#1B3BA0]/10 transition-all duration-300">
                <h2 className="text-xl font-bold text-[#1B3BA0] mb-4 flex items-center">
                  <Phone className="w-6 h-6 text-[#1B3BA0] mr-2" />
                  {language === 'ru' ? 'Телефон' : 
                   language === 'en' ? 'Phone' : 
                   '电话'}
                </h2>
                <a 
                  href="tel:+79104773184"
                  className="text-xl text-gray-800 block mb-2 hover:text-[#1B3BA0] transition-colors"
                >
                  +7 910 477-31-84

                </a>
                <p className="text-gray-600">
                  {language === 'ru' ? 'Пн–Вс: 9:00 — 18:00' : 
                   language === 'en' ? 'Mon–Sun: 9:00 AM — 6:00 PM' : 
                   '周一至周日: 9:00 — 18:00'}
                </p>
              </div>

              {/* Email */}
              <div className="bg-[#1B3BA0]/5 rounded-2xl p-8 hover:bg-[#1B3BA0]/10 transition-all duration-300">
                <h2 className="text-xl font-bold text-[#1B3BA0] mb-4 flex items-center">
                  <Mail className="w-6 h-6 text-[#1B3BA0] mr-2" />
                  Email
                </h2>
                <a 
                  href="mailto:wangban356@163.com"
                  className="text-xl text-gray-800 hover:text-[#1B3BA0] transition-colors"
                >
                  support@kitlin.ru
                </a>
              </div>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* Messengers */}
      <section className="py-32 px-6 bg-gradient-to-b from-white to-[#1B3BA0]/5">
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#1B3BA0] mb-16">
            {language === 'ru' ? 'Связаться в мессенджерах' : 
             language === 'en' ? 'Contact via Messengers' : 
             '通过即时通讯工具联系'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Telegram */}
            <a
              href="https://t.me/6069Cargo"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-8 text-center hover:transform hover:-translate-y-1 transition-all duration-300 shadow-xl hover:shadow-2xl"
            >
              <div className="w-16 h-16 bg-[#1B3BA0] rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1B3BA0] mb-2">Telegram</h3>
              <p className="text-gray-600">
                {language === 'ru' ? 'Открыть чат' : 
                 language === 'en' ? 'Open chat' : 
                 '打开聊天'}
              </p>
            </a>

            {/* WhatsApp */}
            <a
              href="https://wa.me/860"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-8 text-center hover:transform hover:-translate-y-1 transition-all duration-300 shadow-xl hover:shadow-2xl"
            >
              <div className="w-16 h-16 bg-[#1B3BA0] rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1B3BA0] mb-2">WhatsApp</h3>
              <p className="text-gray-600">
                {language === 'ru' ? 'Написать в WhatsApp' : 
                 language === 'en' ? 'Write in WhatsApp' : 
                 '在WhatsApp上写信'}
              </p>
            </a>

            {/* WeChat */}
            <button
              onClick={() => setShowQRCode(true)}
              className="bg-white rounded-2xl p-8 text-center hover:transform hover:-translate-y-1 transition-all duration-300 shadow-xl hover:shadow-2xl"
            >
              <div className="w-16 h-16 bg-[#1B3BA0] rounded-full flex items-center justify-center mx-auto mb-4">
                <QrCode className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1B3BA0] mb-2">WeChat</h3>
              <p className="text-gray-600">
                {language === 'ru' ? 'Показать QR-код' : 
                 language === 'en' ? 'Show QR code' : 
                 '显示二维码'}
              </p>
            </button>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-32 px-6 bg-[#1B3BA0]">
        <div className="max-w-[780px] mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {language === 'ru' ? 'Остались вопросы?' : 
             language === 'en' ? 'Any Questions?' : 
             '有问题吗？'}
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            {language === 'ru' 
              ? 'Заполните форму, и мы свяжемся с вами в ближайшее время'
              : language === 'en' 
              ? 'Fill out the form, and we will contact you shortly'
              : '填写表格，我们将很快与您联系'}
          </p>
          <RequestForm source={language === 'ru' ? "Контакты" : language === 'en' ? "Contacts" : "联系方式"} />
        </div>
      </section>

      {/* WeChat QR Code Modal */}
      {showQRCode && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6"
          onClick={() => setShowQRCode(false)}
        >
          <div 
            className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-[#1B3BA0] mb-6">
              {language === 'ru' ? 'Отсканируйте QR-код в WeChat' : 
               language === 'en' ? 'Scan QR code in WeChat' : 
               '在微信中扫描二维码'}
            </h3>
            <img
              src="https://i.ibb.co/mCKYHXb0/a45a75f5ddb55c869650d82a6b3e7879.jpg"
              alt="WeChat QR"
              className="w-48 h-48 object-cover rounded-xl shadow-xl mx-auto mb-6"
            />
            <button
              onClick={() => setShowQRCode(false)}
              className="w-full bg-[#1B3BA0] text-white py-3 rounded-xl hover:bg-[#0F2875] transition-colors font-medium shadow-md hover:shadow-lg"
            >
              {language === 'ru' ? 'Закрыть' : 
               language === 'en' ? 'Close' : 
               '关闭'}
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes gradient-wave {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        .animate-gradient-wave {
          animation: gradient-wave 6s ease-in-out infinite;
          background-size: 200% auto;
        }
      `}</style>
    </div>
  );
}

export default ContactsPage;